import React, { useState } from 'react';
import { Typography, Box, Paper, CircularProgress } from '@mui/material';
import {
  Rocket,
  PiggyBank,
  Sparkles,
  Receipt,
  Star,
  Link2,
  Video,
} from 'lucide-mui';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../reducers';
import { planPricesPerMonth } from '../../../constants/prices';
import { usePlan } from '../../plan/hooks/plan';
import { createOrUpdateSubscription } from '../../plan/thunk';
import { pushModal } from '../../globalModals/globalModalsSlice';
import planFeatures from '../../plan/upgrade/upgradeView/featureLimits';
import ActionDialog from '../../globalModals/ActionDialog';
import type { ActionType } from '../../globalModals/ActionDialog';
import { setAlertMessage } from '../dashboardSlice';

const UpSellModal = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const source = useSelector(
    (state: RootState) => state.globalModals.actionData.source
  );

  const { term, price: currentPrice } = useSelector(
    (state: RootState) => state.plan.subscription
  );
  const { packageProperties, upgrade } = useSelector(
    (state: RootState) => state.plan
  );
  const { sourceFeatureKey } = upgrade;
  const currentPricePerMonth =
    term === 'monthly' ? currentPrice : currentPrice / 12;
  const { team } = useSelector((state: RootState) => state);
  const teamId = team.selectedTeam?.teamId || team.teams[0].teamId;
  const { teamPlan, isPlan } = usePlan();

  const [isLoading, setIsLoading] = useState(false);

  // Don't show upsell modal to enterprise users
  if (isPlan(['ENTERPRISE'])) {
    onClose();
    return null;
  }

  const dialogType =
    // eslint-disable-next-line no-nested-ternary
    source === 'limits'
      ? 'unlimited'
      : term === 'monthly'
      ? 'annual'
      : 'unlimited';

  // Get the feature info if this is a feature-specific upsell
  const featureInfo =
    sourceFeatureKey && source === 'limits'
      ? planFeatures.BUSINESS?.[sourceFeatureKey]
      : null;

  const dialogContent = [
    {
      type: 'annual',
      title: t('drawer.upsell.annual.title'),
      content: (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 3,
              color: 'text.secondary',
              textAlign: 'center',
              fontWeight: 'normal',
            }}
          >
            {t('drawer.upsell.annual.subtitle')}
          </Typography>
          {currentPricePerMonth && currentPricePerMonth > 0 && (
            <Typography
              variant="h5"
              color="success.main"
              sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3,
              }}
            >
              {t('drawer.upsell.annual.savePercent', {
                percent: Math.round(
                  ((currentPricePerMonth -
                    (teamPlan in planPricesPerMonth
                      ? planPricesPerMonth[
                          teamPlan as keyof typeof planPricesPerMonth
                        ].annual
                      : 0)) /
                    currentPricePerMonth) *
                    100
                ),
              })}
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              mb: 3,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: 'none',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <PiggyBank sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.annual.features.discount.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('drawer.upsell.annual.features.discount.description')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Sparkles sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.annual.features.credits.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('drawer.upsell.annual.features.credits.description')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Receipt sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.annual.features.billing.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('drawer.upsell.annual.features.billing.description')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Star sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.annual.features.features.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('drawer.upsell.annual.features.features.description')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </>
      ),
      newPrice:
        teamPlan in planPricesPerMonth
          ? planPricesPerMonth[teamPlan as keyof typeof planPricesPerMonth]
              .annual
          : 0,
    },
    {
      type: 'unlimited',
      title: featureInfo
        ? t('drawer.upsell.unlimited.unlockFeature', {
            feature: featureInfo.title,
          })
        : t('drawer.upsell.unlimited.title'),
      content: (
        <>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 3,
              color: 'text.secondary',
              textAlign: 'center',
              fontWeight: 'normal',
            }}
          >
            {featureInfo
              ? featureInfo.description
              : t('drawer.upsell.unlimited.subtitle')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 3,
              mb: 3,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: 'none',
              }}
            >
              {featureInfo && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {t('drawer.upsell.unlimited.alsoGet')}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Sparkles sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.unlimited.features.aiCredits.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'drawer.upsell.unlimited.features.aiCredits.description',
                        {
                          limit: packageProperties?.askAICount || 'limited',
                        }
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Link2 style={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.unlimited.features.integrations.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'drawer.upsell.unlimited.features.integrations.description'
                      )}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Video sx={{ color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      {t('drawer.upsell.unlimited.features.meetBot.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'drawer.upsell.unlimited.features.meetBot.description'
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        </>
      ),
      newPrice: planPricesPerMonth.BUSINESS[term],
    },
  ];

  const currentDialog = dialogContent.find(
    (dialog) => dialog.type === dialogType
  );

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const newPlan = dialogType === 'annual' ? teamPlan : 'BUSINESS';
      const newTerm = dialogType === 'annual' ? 'annual' : term;
      const newPrice =
        (currentDialog?.newPrice ?? 0) * (dialogType === 'annual' ? 12 : 1);

      const result = await dispatch(
        createOrUpdateSubscription({
          teamId,
          plan: newPlan,
          term: newTerm,
          totalAmount: newPrice,
        })
      );
      if (createOrUpdateSubscription.rejected.match(result as never)) {
        onClose();
        const { message = '' } = (result as any).error || {};
        throw new Error(message);
      }

      onClose();
      dispatch(
        pushModal({
          type: 'upgradeSuccess',
        })
      );
    } catch (error) {
      console.error('Upgrade failed:', error);
      dispatch(
        setAlertMessage({
          type: 'error',
          message: t('drawer.upsell.pricing.error.message', {
            error: error.message,
          }),
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSavings = () => {
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        const monthlySavings =
          (currentPricePerMonth ?? 0) - (currentDialog.newPrice ?? 0);
        const yearlySavings = monthlySavings * 12;
        return (
          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: 'none',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ mb: 0.5, fontWeight: 'medium' }}
                >
                  {t('drawer.upsell.pricing.yearlySavings')}
                </Typography>
                <Typography
                  variant="h4"
                  color="success.main"
                  sx={{ fontWeight: 'bold' }}
                >
                  ${yearlySavings}
                </Typography>
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('drawer.upsell.pricing.currentMonthly')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {t('drawer.upsell.pricing.perMonth', {
                      price: currentPricePerMonth,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t('drawer.upsell.pricing.newMonthly')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {t('drawer.upsell.pricing.perMonth', {
                      price: currentDialog.newPrice,
                    })}
                  </Typography>
                </Box>
              </Box>
            </Paper>
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ width: '100%' }}
            >
              {t('drawer.upsell.pricing.billedAnnually', {
                price: currentDialog.newPrice * 12,
              })}
            </Typography>
          </Box>
        );
      }
      if (dialogType === 'unlimited') {
        return (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2">
                  {t('drawer.upsell.pricing.currentPlan')}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6">
                    ${currentPricePerMonth}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5, color: 'text.secondary' }}
                    >
                      {t('drawer.upsell.pricing.month')}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle2">
                  {t('drawer.upsell.pricing.unlimitedPlan')}
                </Typography>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" color="primary.main">
                    ${currentDialog.newPrice}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 0.5, color: 'text.secondary' }}
                    >
                      {t('drawer.upsell.pricing.month')}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </Paper>
            {term === 'annual' && (
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
              >
                {t('drawer.upsell.pricing.billedAnnually', {
                  price: currentDialog.newPrice * 12,
                })}
              </Typography>
            )}
          </Box>
        );
      }
    }
    return null;
  };

  const getButtonText = () => {
    if (dialogType === 'annual') {
      return t('drawer.upsell.buttons.switchAnnual');
    }
    if (dialogType === 'unlimited') {
      return t('drawer.upsell.buttons.upgradeUnlimited');
    }
    return '';
  };

  const getDialogTitle = () => {
    if (currentDialog && 'newPrice' in currentDialog) {
      if (dialogType === 'annual') {
        return t('drawer.upsell.annual.title');
      }
      return currentDialog.title;
    }
    return '';
  };

  const actions: ActionType[] = [
    {
      key: 'cancel',
      label: t('drawer.upsell.buttons.cancel'),
      onClick: onClose,
      variant: 'outlined',
      color: 'secondary',
      disabled: isLoading,
    },
    {
      key: 'upgrade',
      label: getButtonText(),
      onClick: handleUpgrade,
      variant: 'contained',
      color: 'primary',
      disabled: isLoading,
      loading: isLoading,
      startIcon: isLoading ? (
        <CircularProgress size={20} color="inherit" />
      ) : (
        <Rocket />
      ),
    },
  ];

  return (
    <ActionDialog
      open
      onClose={onClose}
      title={getDialogTitle()}
      actions={actions}
      maxWidth="sm"
    >
      {currentDialog?.content}
      {calculateSavings()}
    </ActionDialog>
  );
};

export default UpSellModal;
